/**
 * Bounded session activation (2026-08-29, mobile sign-in P1).
 *
 * Root cause of the "Signing in..." forever spinner (owner, mobile, prod
 * 20:46 UTC): Clerk's `setActive` awaits `window.__unstable__onBeforeSetActive`,
 * which in @clerk/nextjs calls a Next.js SERVER ACTION (`invalidateCacheAction`)
 * and resolves ONLY on success - `void invalidateCacheAction().then(resolve)`,
 * no catch. A tab loaded before a deploy carries a server-action id the new
 * deployment no longer knows ("Failed to find Server Action ... older or newer
 * deployment" - the exact web log line at 20:46:28), the action rejects, and
 * `setActive` never resolves. The session cookie is already set by then, so
 * the user IS signed in - they just never leave the spinner. We deploy many
 * times a day and shoppers keep tabs open for days: this hits every device.
 *
 * Contract: `activateSession` NEVER hangs. It races `setActive` against a
 * bound; on timeout it checks whether Clerk actually has the session (it
 * does in the stale-deployment case) and, if so, treats the sign-in as
 * complete and performs a hard reload - which both lands the browser on the
 * current deployment and refreshes server-side auth state (what the failed
 * invalidate action was for). No session after the bound = an honest error
 * the card can show. The normal (non-stale) path is byte-identical: setActive
 * resolves first and the race is a no-op.
 */

export type ActivationOutcome = "activated" | "activated_stale_reload";

type SetActiveFn = (params: { session: string }) => Promise<unknown>;

declare global {
  interface Window {
    Clerk?: {
      session?: { id?: string | null } | null;
      user?: unknown;
    };
  }
}

const DEFAULT_TIMEOUT_MS = 7000;

function clerkHasSession(sessionId: string): boolean {
  try {
    const c = window.Clerk;
    if (!c) return false;
    if (c.session?.id && c.session.id === sessionId) return true;
    return Boolean(c.user);
  } catch {
    return false;
  }
}

export async function activateSession(opts: {
  setActive: SetActiveFn;
  sessionId: string;
  timeoutMs?: number;
}): Promise<ActivationOutcome> {
  const { setActive, sessionId, timeoutMs = DEFAULT_TIMEOUT_MS } = opts;
  let timer: ReturnType<typeof setTimeout> | null = null;
  const bound = new Promise<"timeout">((resolve) => {
    timer = setTimeout(() => resolve("timeout"), timeoutMs);
  });
  try {
    const winner = await Promise.race([
      setActive({ session: sessionId }).then(() => "done" as const),
      bound,
    ]);
    if (winner === "done") return "activated";
  } finally {
    if (timer) clearTimeout(timer);
  }

  // Bound hit: the activation hook stalled (stale-deployment server action,
  // slow network). If Clerk already holds the session the sign-in IS done.
  if (clerkHasSession(sessionId)) {
    // Let the caller close its UI first, then land on the current deployment.
    setTimeout(() => {
      try {
        window.location.reload();
      } catch {
        // ignore
      }
    }, 0);
    return "activated_stale_reload";
  }
  throw new Error(
    "Signing in is taking longer than expected. Please try again.",
  );
}
